import { notify } from '@billy/design-system'
import { trackError } from '@billy/track-error'
import { useCallback, useMemo, useState } from 'react'
import { FileRejection } from 'react-dropzone'
import { useTranslation } from 'react-i18next'
import { useMutation, useQueryClient } from 'react-query'

import { UploadFilesErrorMessage, UploadFilesErrors } from '@features/attachments/elements/UploadFilesErrorMessage'
import { useUserOrganization } from '@modules-deprecated/app/organization'
import { useCurrentUser } from '@modules-deprecated/app/user'

import { NotificationKeys } from '../../../../../enums/notificationKeys'
import { QueryKeys } from '../../../../../enums/queryKeys'
import { APIError } from '../../../../../utils'
import { CreateFolderActionOriginType } from '../../../enums/createFolderActionOriginType'
import { createDocument } from '../../../query-api'

interface UploadProgress {
  current: number
  total: number
}

const uploadProgressInitialData: UploadProgress = {
  current: 0,
  total: 0,
}

interface UseUploadDocumentsProps {
  folderId: string | undefined
  cacheKey?: string
}

interface UploadDocumentsPayload {
  files: File[]
  folderId?: string
  rejectedFiles?: FileRejection[]
}

export const useUploadDocuments = (props: UseUploadDocumentsProps) => {
  const { folderId, cacheKey } = props
  const { t } = useTranslation()
  const { organization } = useUserOrganization()
  const { user } = useCurrentUser()
  const queryClient = useQueryClient()
  const [uploadProgress, setUploadProgress] = useState<UploadProgress>(uploadProgressInitialData)

  const handleUploadMutation = useCallback(
    async (payload: UploadDocumentsPayload) => {
      const { files, rejectedFiles, folderId: targetFolderId = folderId } = payload
      const uploadErrors: UploadFilesErrors = {}

      if (rejectedFiles) {
        for (const { file, errors } of rejectedFiles) {
          uploadErrors[file.name] = errors.map((e) => e.message).join(', ')
        }
      }

      const totalFilesCount = files.length + (rejectedFiles?.length ?? 0)

      if (!organization?.id || !user?.id) {
        notify({
          id: NotificationKeys.UploadFiles,
          message: t('file.rejected'),
          variant: 'error',
        })
        trackError('"organizationId" and "userId" have to be provided to upload a document')

        return 0
      }

      for (const file of files) {
        setUploadProgress((prev) => ({
          current: prev.current + 1,
          total: totalFilesCount,
        }))

        try {
          await createDocument({
            organizationId: organization.id,
            payload: {
              file,
              folderId: targetFolderId,
              externalUserId: user.id,
              origin: CreateFolderActionOriginType.BO,
            },
          })
        } catch (error: unknown) {
          const errorMessage = (error as APIError)?.body?.errorMessage ?? t('file.rejected.unknown_error')
          uploadErrors[file.name] = errorMessage
        }
      }

      const rejectedFilesCount = Object.keys(uploadErrors).length

      if (rejectedFilesCount > 0) {
        notify({
          id: NotificationKeys.UploadFiles,
          message: UploadFilesErrorMessage({ uploadFilesErrors: uploadErrors }),
          title: t('file.rejected.count', { count: rejectedFilesCount }),
          variant: rejectedFilesCount === totalFilesCount ? 'error' : 'warning',
        })
      }

      return totalFilesCount - rejectedFilesCount
    },
    [organization, user, folderId, t],
  )

  const { isLoading: isUploading, mutate: upload } = useMutation(handleUploadMutation, {
    onSettled: () => {
      setUploadProgress(uploadProgressInitialData)
    },
    onSuccess: (successCount, variables) => {
      const { folderId: targetFolderId = folderId } = variables

      if (successCount) {
        notify({
          id: NotificationKeys.UploadFiles,
          message: t('file.uploaded'),
          variant: 'success',
        })
      }
      queryClient.invalidateQueries([QueryKeys.DmsFolderContents, cacheKey ?? targetFolderId])
    },
  })

  const uploadDocuments = useCallback(
    (files: File[], rejectedFiles?: FileRejection[], targetFolderId?: string) => {
      upload({ files, folderId: targetFolderId, rejectedFiles })
    },
    [upload],
  )

  return useMemo(
    () => ({
      uploadDocuments,
      isUploading,
      progress: uploadProgress,
    }),
    [uploadDocuments, isUploading, uploadProgress],
  )
}
